/* @flow */

import Typenames from './Typenames'
import { ActionTypes, store } from 'store'
import _ from 'lodash'

/**
 * Setting represents a configuration for a group of Manatees
 * @typedef { Object } Setting
 * @memberof SettingService
 */
export type Setting = {
  id?: string,
  key?: string,
  version?: string,
  forVersion: string,
  forGroup: string,
  settings: Object
}

const defaultDescriptive = Object.freeze({
  description: '',
  key: ''
})

/**
 * Settings typename
 * @private
 * @memberof SettingService
 */
const TYPENAME = Typenames.MANATEESETTINGS

const uniqueConstraints = []

/**
 * Creates a new setting.
 * @function
 * @memberof SettingService
 *
 * @return { Promise<string> } The id of the added setting.
 */
const createSetting = (setting: Setting): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.CREATE, {
    typename: TYPENAME,
    item: setting,
    uniqueConstraints: uniqueConstraints
  })
}

/**
 * Reads a setting, flattening 'descriptive'.
 * @function
 * @memberof SettingService
 *
 * @return { Promise<Setting> } The settings object.
 */
const readSetting = (id: string): Promise<Object> => {
  return store.dispatch(ActionTypes.GET_BY_ID, { typename: TYPENAME, id: id }).then(field => {
    return { ...defaultDescriptive, ...field }
  })
}

/**
 * Updates a setting.
 * @function
 * @memberof SettingService
 *
 * @return { Promise<any> }
 */
const updateSetting = (id: string, setting: Setting): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.UPDATE, {
    typename: TYPENAME,
    id: id,
    item: setting,
    uniqueConstraints: uniqueConstraints
  })
}

/**
 * Deletes a setting.
 * @function
 * @memberof SettingService
 *
 * @return { Promise<any> }
 */
const deleteSetting = (id: string): Promise<Object> => {
  return store.dispatch(ActionTypes.DELETE, { typename: TYPENAME, id: id })
}

/**
 * Reads all settings flattening descriptive.
 * @function
 * @memberof SettingService
 *
 * @return { Observable<Array<Setting>> } All the settings.
 */
const readAllSettings = (): Promise<Object> => {
  return store.dispatch(ActionTypes.GET, TYPENAME).then(settings => {
    return _.map(settings, g => {
      return {
        ...defaultDescriptive,
        ...g
      }
    })
  })
}

/**
 * Copies a setting.
 * @function
 * @memberof SettingsService
 *
 * @return { Promise<string> }
 */
const copySetting = (id: string, nameSuffix: string): Promise<PostResponse> => {
  return self.readSetting(id).then(setting => {
    const toCopy = { ...setting }
    delete toCopy.id
    delete toCopy.forVersion
    delete toCopy.forGroup

    return self.createSetting(toCopy)
  })
}

const self = {
  createSetting,
  readSetting,
  updateSetting,
  deleteSetting,
  readAllSettings
}

/**
 * Setting service: CRUD interface for settings.
 * @module SettingService
 * @namespace SettingService
 * @version 1.0.0
 */
export default self
